import React from 'react';
import { Box, Paper, Stack, Typography, IconButton, Divider, Tooltip, Tabs, Tab } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { styled } from '@mui/material/styles';
import * as FsCategoryService from '@/services/accountService/fsCategoryService';
import { HoverLabelTextField, SnackbarField } from '@/components/common';
import { AuditCompanyFSCategoryService } from '@/services/auditService';

export interface FsEditorPreviewProps {
  companyId?: number;
  companyName?: string;
}

interface EditRowState {
  fsId?: number;
  aliasEn?: string;
  aliasZh?: string;
  /** Snapshot for Cancel revert */
  prevAliasEn?: string;
  prevAliasZh?: string;
}

interface FlatNode {
  fsId?: number;
  parentId?: number | null;
  level: number;
  code?: string;
  nameEn?: string;
  nameZh?: string;
  aliasEn?: string;
  aliasZh?: string;
  children?: FsCategoryService.FsCategory[];
  raw: FsCategoryService.FsCategory;
}

function toFlatList(nodes: FsCategoryService.FsCategory[], level = 0, parentId: number | null = null): FlatNode[] {
  const out: FlatNode[] = [];
  for (const n of nodes) {
    out.push({
      fsId: n.fsId,
      parentId,
      level,
      code: n.code,
      nameEn: n.nameEn,
      nameZh: n.nameZh,
      aliasEn: (n as FsCategoryService.FsCategory & { aliasEn?: string })?.aliasEn ?? '',
      aliasZh: (n as FsCategoryService.FsCategory & { aliasZh?: string })?.aliasZh ?? '',
      children: n.children || [],
      raw: n,
    });
    if (n.children && n.children.length) out.push(...toFlatList(n.children, level + 1, (n.fsId as number) ?? null));
  }
  return out;
}

const Row = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Col = styled('div')<{ width?: number; indent?: number }>(({ theme, width, indent = 0 }) => ({
  width: typeof width === 'number' ? width : 'auto',
  flexShrink: typeof width === 'number' ? 0 : 1,
  flexGrow: typeof width === 'number' ? 0 : 1,
  paddingLeft: indent,
  paddingRight: theme.spacing(1),
  minWidth: 0,
}));

const Toolbar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
);

interface PreviewLine {
  fsId?: number;
  level: number;
  label: string;
  isParent: boolean;
}

function flattenPreviewLines(
  nodes: FsCategoryService.FsCategory[],
  level = 0,
  collapsedIds?: Set<number>,
): PreviewLine[] {
  const out: PreviewLine[] = [];
  for (const n of nodes) {
    const isParent = !!(n.children && n.children.length > 0);
    out.push({
      fsId: n.fsId,
      level,
      label: String(n.nameEn || n.nameZh || n.code || n.fsId || ''),
      isParent,
    });
    const collapsed = n.fsId != null && collapsedIds?.has(Number(n.fsId));
    if (isParent && !collapsed) {
      out.push(...flattenPreviewLines(n.children!, level + 1, collapsedIds));
    }
  }
  return out;
}

/**
 * Right-side preview: Paper height matches the left EditableList (row stretch),
 * and flex spacers after each row expand so both panels share the same height.
 * Collapsed parent ids hide descendants (same as left Editable).
 */
const PreviewTree: React.FC<{
  title: string;
  tree: FsCategoryService.FsCategory[];
  headerLines?: string[];
  collapsedIds?: Set<number>;
}> = ({ title, tree, headerLines, collapsedIds }) => {
  const lines = React.useMemo(
    () => flattenPreviewLines(tree, 0, collapsedIds),
    [tree, collapsedIds],
  );

  return (
    <Paper sx={{
      p: 2,
      height: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <Box sx={{ flexShrink: 0 }}>
        <SectionTitle title={title} />
        <Divider sx={{ mb: 1 }} />
        {headerLines && headerLines.length > 0 && (
          <Box sx={{ mb: 1 }}>
            {headerLines.map((line, idx) => (
              <Typography key={idx} variant="body2" sx={{ fontWeight: idx === 0 ? 700 : 400 }}>
                {line}
              </Typography>
            ))}
            <Divider sx={{ my: 1 }} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {lines.map((line) => (
          <React.Fragment key={String(line.fsId ?? `${line.level}-${line.label}`)}>
            <Box sx={{ pl: line.level * 2, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 22 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {line.label}
                </Typography>
                {!line.isParent && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>0</Typography>
                )}
              </Box>
            </Box>
            {/* Flexible gap under each row — expands to match left panel height */}
            <Box sx={{ flex: '1 1 0', minHeight: 4 }} />
          </React.Fragment>
        ))}
      </Box>
    </Paper>
  );
};

const EditableList: React.FC<{
  title: string;
  sourceTree: FsCategoryService.FsCategory[];
  onSave?: (flat: FlatNode[]) => void;
  saving?: boolean;
  collapsedIds: Set<number>;
  onCollapsedChange: (next: Set<number>) => void;
}> = ({ title, sourceTree, onSave, saving, collapsedIds, onCollapsedChange }) => {
  const [list, setList] = React.useState<FlatNode[]>(() => toFlatList(sourceTree));
  const [editing, setEditing] = React.useState<EditRowState | null>(null);
  const [initialFlat, setInitialFlat] = React.useState<FlatNode[]>(() => toFlatList(sourceTree));

  React.useEffect(() => {
    const fl = toFlatList(sourceTree);
    setList(fl);
    setInitialFlat(fl);
    setEditing(null);
    onCollapsedChange(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset collapse when tree source changes
  }, [sourceTree]);

  const moveUp = (idx: number) => {
    if (idx <= 0) return;
    const a = list[idx - 1];
    const b = list[idx];
    if (a.parentId !== b.parentId) return;
    const next = [...list];
    next[idx - 1] = b;
    next[idx] = a;
    setList(next);
  };
  const moveDown = (idx: number) => {
    if (idx >= list.length - 1) return;
    const a = list[idx];
    const b = list[idx + 1];
    if (a.parentId !== b.parentId) return;
    const next = [...list];
    next[idx] = b;
    next[idx + 1] = a;
    setList(next);
  };

  const indent = (idx: number) => {
    if (idx <= 0) return;
    const curr = list[idx];
    const prev = list[idx - 1];
    const next = [...list];
    next[idx] = { ...curr, parentId: prev.fsId ?? null, level: (curr.level ?? 0) + 1 };
    setList(next);
  };
  const outdent = (idx: number) => {
    const curr = list[idx];
    if (curr.parentId == null) return;
    const parentIdx = list.findIndex(n => n.fsId === curr.parentId);
    const parent = list[parentIdx];
    const next = [...list];
    next[idx] = { ...curr, parentId: parent?.parentId ?? null, level: Math.max(0, (curr.level ?? 0) - 1) };
    setList(next);
  };

  const startEdit = (row: FlatNode) =>
    setEditing({
      fsId: row.fsId,
      aliasEn: (row.aliasEn && row.aliasEn.length > 0) ? row.aliasEn : (row.nameEn || ''),
      aliasZh: (row.aliasZh && row.aliasZh.length > 0) ? row.aliasZh : (row.nameZh || ''),
      prevAliasEn: row.aliasEn || '',
      prevAliasZh: row.aliasZh || '',
    });

  const cancelEdit = () => {
    if (!editing) return;
    const next = list.map(n =>
      n.fsId === editing.fsId
        ? { ...n, aliasEn: editing.prevAliasEn, aliasZh: editing.prevAliasZh }
        : n
    );
    setList(next);
    setEditing(null);
  };

  /** Apply alias edits into list as user types (no per-row Save) */
  const updateEditing = (patch: Partial<Pick<EditRowState, 'aliasEn' | 'aliasZh'>>) => {
    setEditing(s => {
      if (!s) return s;
      const next = { ...s, ...patch };
      setList(prev => prev.map(n =>
        n.fsId === next.fsId
          ? { ...n, aliasEn: next.aliasEn ?? '', aliasZh: next.aliasZh ?? '' }
          : n
      ));
      return next;
    });
  };

  const hasChildrenAt = (idx: number) => {
    const curr = list[idx];
    for (let i = idx + 1; i < list.length; i++) {
      if (list[i].level <= curr.level) break;
      if (list[i].level === curr.level + 1) return true;
    }
    return false;
  };
  const toggleCollapse = (idx: number) => {
    const id = list[idx].fsId;
    if (typeof id !== 'number') return;
    const next = new Set(collapsedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onCollapsedChange(next);
  };
  const isHidden = (idx: number) => {
    const curr = list[idx];
    let level = curr.level;
    for (let i = idx - 1; i >= 0; i--) {
      const node = list[i];
      if (node.level < level) {
        const id = node.fsId;
        if (typeof id === 'number' && collapsedIds.has(id)) return true;
        level = node.level;
      }
      if (level === 0) break;
    }
    return false;
  };

  const saveAll = () => {
    setEditing(null);
    onSave?.(list);
  };

  const dirty = React.useMemo(() => {
    const a = initialFlat.map(n => ({ id: n.fsId, p: n.parentId, lvl: n.level, aliasEn: n.aliasEn || '', aliasZh: n.aliasZh || '' }));
    const b = list.map(n => ({ id: n.fsId, p: n.parentId, lvl: n.level, aliasEn: n.aliasEn || '', aliasZh: n.aliasZh || '' }));
    if (a.length !== b.length) return true;
    for (let i = 0; i < a.length; i++) {
      const x = a[i];
      const y = b[i];
      if (x.id !== y.id || x.p !== y.p || x.lvl !== y.lvl || x.aliasEn !== y.aliasEn || x.aliasZh !== y.aliasZh) return true;
    }
    return false;
  }, [initialFlat, list]);

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <SectionTitle title={title} />
        <Tooltip title="Save all changes">
          <span>
            <IconButton color="primary" onClick={saveAll} disabled={!dirty || !!saving}>
              <SaveIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Row>
        <Col width={60}>#</Col>
        <Col>Name (EN)</Col>
      </Row>
      {list.map((row, idx) => isHidden(idx) ? null : (
        <Row key={String(row.fsId ?? idx)}>
          <Col width={60}>{idx + 1}</Col>
          <Col indent={row.level * 16}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                {hasChildrenAt(idx) ? (
                  <IconButton size="small" onClick={() => toggleCollapse(idx)}>
                    {typeof row.fsId === 'number' && collapsedIds.has(row.fsId) ? <KeyboardArrowRightIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </IconButton>
                ) : (
                  <Box sx={{ width: 32 }} />
                )}
                {editing?.fsId === row.fsId ? (
                  <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                    <HoverLabelTextField
                      size="small"
                      fullWidth
                      label="Alias (EN)"
                      value={editing?.aliasEn ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEditing({ aliasEn: e.target.value })}
                    />
                    <HoverLabelTextField
                      size="small"
                      fullWidth
                      label="Alias (ZH)"
                      value={editing?.aliasZh ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEditing({ aliasZh: e.target.value })}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" noWrap title={(row.aliasEn || row.nameEn || '')}>{(row.aliasEn || row.nameEn || '-') }</Typography>
                )}
              </Box>
              <Toolbar>
                <Tooltip title="Move Up"><span><IconButton size="small" onClick={() => moveUp(idx)} disabled={idx === 0}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Move Down"><span><IconButton size="small" onClick={() => moveDown(idx)} disabled={idx === list.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Indent"><span><IconButton size="small" onClick={() => indent(idx)} disabled={idx === 0}><KeyboardTabIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Outdent"><span><IconButton size="small" onClick={() => outdent(idx)} disabled={row.parentId == null}><KeyboardBackspaceIcon fontSize="small" /></IconButton></span></Tooltip>
                {editing?.fsId === row.fsId ? (
                  <Tooltip title="Cancel"><IconButton size="small" color="error" onClick={cancelEdit}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                ) : (
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => startEdit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                )}
              </Toolbar>
            </Box>
          </Col>
        </Row>
      ))}
    </Paper>
  );
};

const CompanyFsEditorPreview: React.FC<FsEditorPreviewProps> = ({ companyId, companyName }) => {
  const [tab, setTab] = React.useState<'BS' | 'PL'>('BS');
  const [bsData, setBsData] = React.useState<AuditCompanyFSCategoryService.AuditBuildCompanyFSTreeItems[]>([]);
  const [plData, setPlData] = React.useState<AuditCompanyFSCategoryService.AuditBuildCompanyFSTreeItems[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savingBS, setSavingBS] = React.useState<boolean>(false);
  const [savingPL, setSavingPL] = React.useState<boolean>(false);
  const [collapsedBs, setCollapsedBs] = React.useState<Set<number>>(() => new Set());
  const [collapsedPl, setCollapsedPl] = React.useState<Set<number>>(() => new Set());
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: 'success' | 'error' } | null>(null);

  const fetchTree = React.useCallback(async (statement: 'BS' | 'PL') => {
    if (!companyId) return [] as AuditCompanyFSCategoryService.AuditBuildCompanyFSTreeItems[];
    const res = await AuditCompanyFSCategoryService.buildCompanyFSTree(Number(companyId), statement);
    return (Array.isArray(res) ? res : []) as AuditCompanyFSCategoryService.AuditBuildCompanyFSTreeItems[];
  }, [companyId]);

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!companyId) return;
      try {
        setLoading(true);
        setError(null);
        const data = await fetchTree('BS');
        if (!alive) return;
        setBsData(data);
      } catch (e: unknown) {
        if (alive) setError(e instanceof Error ? e.message : 'Failed to load Balance Sheet');
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [companyId, fetchTree]);

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!companyId) return;
      if (tab === 'PL' && plData.length === 0) {
        try {
          setLoading(true);
          setError(null);
          const data = await fetchTree('PL');
          if (!alive) return;
          setPlData(data);
        } catch (e: unknown) {
          if (alive) setError(e instanceof Error ? e.message : 'Failed to load Profit & Loss');
        } finally {
          if (alive) setLoading(false);
        }
      }
    };
    run();
    return () => { alive = false; };
  }, [tab, companyId, plData.length, fetchTree]);

  const handleSave = React.useCallback(async (statement: 'BS' | 'PL', flat: FlatNode[]) => {
    if (!companyId) return;
    const setSaving = statement === 'BS' ? setSavingBS : setSavingPL;
    try {
      setSaving(true);
      const payload: AuditCompanyFSCategoryService.AuditCompanyFSCategoryPayload[] = [];
      let order = 1;
      const originalTree = statement === 'BS' ? bsData : plData;
      const originalParent = new Map<number, number | null>();
      const buildOrig = (nodes: FsCategoryService.FsCategory[], p: number | null) => {
        for (const n of nodes) {
          if (n.fsId != null) originalParent.set(Number(n.fsId), p);
          if (n.children?.length) buildOrig(n.children, n.fsId != null ? Number(n.fsId) : null);
        }
      };
      buildOrig(originalTree || [], null);
      for (const row of flat) {
        if (row.fsId == null) continue;
        const idNum = Number(row.fsId);
        const currentParent: number | null = row.parentId != null ? Number(row.parentId) : null;
        const original = originalParent.get(idNum) ?? null;
        const parentOverride: number | null = (currentParent !== original)
          ? (currentParent == null ? 0 : currentParent)
          : null;
        payload.push({
          companyId: Number(companyId),
          fsId: idNum,
          statement,
          aliasEn: row.aliasEn || '',
          aliasZh: row.aliasZh || '',
          parentFsIdOverride: parentOverride,
          displayOrder: order++,
          isVisible: 1,
          isActive: 1,
        });
      }

      await AuditCompanyFSCategoryService.batchReplace(Number(companyId), statement, payload);
      setSnackbar({ open: true, message: 'Saved successfully', severity: 'success' });
      const latest = await fetchTree(statement);
      if (statement === 'BS') setBsData(latest); else setPlData(latest);
    } catch (e: unknown) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [companyId, fetchTree, bsData, plData]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 1, flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="BS" label="Balance Sheet" />
        <Tab value="PL" label="Profit & Loss" />
      </Tabs>
      {loading && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, flexShrink: 0 }}>Loading...</Typography>
      )}
      {error && (
        <Typography variant="body2" sx={{ color: 'error.main', mb: 1, flexShrink: 0 }}>{error}</Typography>
      )}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {tab === 'BS' ? (
          <Stack direction="row" spacing={2} alignItems="stretch">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <EditableList
                title={'Editable - Balance Sheet'}
                sourceTree={bsData}
                saving={savingBS}
                onSave={(flat) => handleSave('BS', flat)}
                collapsedIds={collapsedBs}
                onCollapsedChange={setCollapsedBs}
              />
            </Box>
            <Box sx={{ width: 1, maxWidth: 24, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <PreviewTree
                title="Balance Sheet Preview"
                tree={bsData}
                collapsedIds={collapsedBs}
                headerLines={[
                  companyName || String(companyId || ''),
                  'Balance sheet items',
                  'As at date',
                ]}
              />
            </Box>
          </Stack>
        ) : (
          <Stack direction="row" spacing={2} alignItems="stretch">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <EditableList
                title={'Editable - Profit & Loss'}
                sourceTree={plData}
                saving={savingPL}
                onSave={(flat) => handleSave('PL', flat)}
                collapsedIds={collapsedPl}
                onCollapsedChange={setCollapsedPl}
              />
            </Box>
            <Box sx={{ width: 1, maxWidth: 24, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <PreviewTree
                title="Profit & Loss Preview"
                tree={plData}
                collapsedIds={collapsedPl}
                headerLines={[
                  companyName || String(companyId || ''),
                  'Details income statement',
                  '(For management information purpose only)',
                  'For the period/ year ended date',
                ]}
              />
            </Box>
          </Stack>
        )}
      </Box>
      <SnackbarField
        open={!!snackbar?.open}
        message={snackbar?.message || ''}
        severity={snackbar?.severity}
        onClose={() => setSnackbar(null)}
      />
    </Box>
  );
};

export default CompanyFsEditorPreview;
export { CompanyFsEditorPreview };
